/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from "react";

import { capitalizeEachWord } from "@/helpers/string";
import { AnalysisDatasetSummary } from "@/types/analysisSummary";

/**
 * Custom hook to generate columns, rows, donutChartData, and barChartData
 * from the summary data.
 */
export function useAnalysisSummaryVisualization(
  summaryData: AnalysisDatasetSummary | undefined,
  highestScoreGridId: string
) {
  // Memoized columns
  const columns = useMemo(() => {
    if (!summaryData) return [];
    return summaryData.columns.map((col) => ({
      id: col.name,
      accessorKey: col.name,
      header: col.name
    }));
  }, [summaryData]);

  // Memoized rows
  const rows = useMemo(() => {
    if (!summaryData) return [];

    return summaryData.rows
      .map((row) => {
        const rowObj: Record<string, any> = {};
        summaryData.columns.forEach((col, idx) => {
          let value = row[idx] || "null";
          if (col.name === "Label" && typeof value === "string") {
            value = capitalizeEachWord(value || "null", "_");
          }
          rowObj[col.name] =
            typeof value === "number" ? value.toLocaleString() : value;
        });

        const nonLabelKeys = Object.keys(rowObj).filter(
          (key) => key !== "Label"
        );
        // Remove rows where all non-label keys are zero
        const allZero =
          nonLabelKeys.length > 0 &&
          nonLabelKeys.every((key) => rowObj[key] === "0" || rowObj[key] === 0);

        if (allZero) return {};
        return rowObj;
      })
      .filter((rowObj) => Object.keys(rowObj).length > 0); // Remove empty object
  }, [summaryData]);

  // Memoized donut chart data
  const donutChartData = useMemo(() => {
    if (!summaryData) return [];

    return summaryData.rows.map((row) => {
      const rowObj: Record<string, any> = {};

      const highestScoreGridRowIndex = summaryData.columns.findIndex(
        (col) => col.name === highestScoreGridId
      );

      summaryData.columns.forEach((_, idx) => {
        // this a trick to handle if the category if series get from `highestScoreGridRowIndex` if not series use index
        const rowValue = row[highestScoreGridRowIndex] ?? row[idx];
        if (idx === 0) {
          if (typeof rowValue === "string") {
            rowObj["name"] = capitalizeEachWord(rowValue || "null", "_");
          } else if (typeof rowValue === "number") {
            rowObj["name"] = rowValue.toLocaleString();
          } else {
            rowObj["name"] = rowValue ?? "null";
          }
        } else {
          rowObj["value"] = rowValue;
        }
      });
      return rowObj;
    });
  }, [summaryData, highestScoreGridId]);

  // Memoized bar chart data
  const barChartData = useMemo(() => {
    if (!summaryData) return [];
    const labelIndex = 0;
    const valueIndexes = summaryData.columns
      .map((_, idx) => idx)
      .filter((idx) => idx !== labelIndex);

    return summaryData.rows.map((row) => {
      let label = row[labelIndex] ?? "";
      if (typeof label === "string") {
        label = capitalizeEachWord(label?.toString() || "null", "_");
      } else if (typeof label === "number") {
        label = label.toLocaleString();
      }
      const obj: any = { name: label };
      valueIndexes.forEach((idx) => {
        const colName = summaryData.columns[idx].name;
        obj[colName] = row[idx] ?? null;
      });
      return obj;
    });
  }, [summaryData]);

  return { columns, rows, donutChartData, barChartData };
}

export default useAnalysisSummaryVisualization;
